import {
  BarcodeOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Descriptions,
  Input,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ModalRestockWarehouse from "../../components/modal/Warehouse/ModalRestockWarehouse";
import { PriorityMark, QCStatusProcess } from "../../enum/common";
import {
  IFilterWarehouse,
  IPartNumberGroupDtoWithPartNumbers,
} from "../../models/Warehouse";
import {
  useRequestQcForWarehouseGroup,
  useWarehouseGroup,
  useWarehouseGroupById,
} from "../../query/WarehouseQuery";
import { getQCProcessStatus, renderQCStatusTag } from "../../utils/qcHelper";
import { notify } from "../../utils/toast";

const WarehouseListImport: React.FC = () => {
  const [selectedImportId, setSelectedImportId] = useState<number | null>(null);
  const [filter, setFilter] = useState<IFilterWarehouse>({
    page: 1,
    limit: 10,
  });
  const [priority, setPriority] = useState<string>("");
  const [restockModalOpen, setRestockModalOpen] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const awbParam = searchParams.get("awb");

  useEffect(() => {
    if (awbParam) {
      setFilter((prev) => ({ ...prev, awb: awbParam, page: 1 }));
    }
  }, [awbParam]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setFilter((prev) => ({ ...prev, awb: value, page: 1 }));
    }, 300),
    [],
  );

  const { data: importGroupList, isLoading: importGroupLoading } =
    useWarehouseGroup(filter);
  const importGroups = importGroupList?.data ?? [];
  const { data: selectedImport, isLoading: selectedImportLoading } =
    useWarehouseGroupById(selectedImportId!);

  const requestQcMutation = useRequestQcForWarehouseGroup();
  const handleQCRequest = async (
    selectedImport: IPartNumberGroupDtoWithPartNumbers | null,
  ) => {
    if (selectedImport && !selectedImport.qcRequested && priority) {
      try {
        await requestQcMutation.mutateAsync(
          {
            id: selectedImport.id,
            priority: priority,
          },
          {
            onSuccess: () => {
              notify.success("Yêu cầu QC thành công");
            },
          },
        );
      } catch (error: any) {
        const message = error?.response?.data?.message || error.message;
        notify.error(message || "Lỗi yêu cầu QC");
      }
    } else {
      notify.error("Vui lòng chọn mức độ ưu tiên trước khi yêu cầu QC");
    }
  };

  const detailColumns = [
    {
      title: "Mã hàng",
      dataIndex: "partNumber",
      key: "partNumber",
    },
    {
      title: "Mã dự án",
      dataIndex: "projectCode",
      key: "projectCode",
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Mã kho",
      dataIndex: ["location", "warehouse", "warehouseCode"],
      key: "warehouseCode",
    },
    {
      title: "Mã nội bộ",
      dataIndex: "internalCode",
      key: "internalCode",
    },
    {
      title: "Số lượng hóa đơn",
      dataIndex: "invoiceQuantity",
      key: "invoiceQuantity",
    },
    {
      title: "Số lượng thực tế",
      dataIndex: "actualQuantity",
      key: "actualQuantity",
    },
    {
      title: "Vị trí kho",
      dataIndex: ["location", "code"],
      key: "location",
    },

    {
      title: "Đơn vị tính",
      dataIndex: "unit",
      key: "unit",
    },

    {
      title: "Trạng thái QC",
      dataIndex: "qcStatus",
      key: "qcStatus",
      render: (status: string) => renderQCStatusTag(status),
    },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="w-full lg:w-[350px] xl:w-[428px]">
        <Card size="small" className="mb-4">
          <Space direction="vertical" className="w-full" size="middle">
            <Input
              placeholder="Tìm kiếm theo AWB"
              prefix={<SearchOutlined />}
              allowClear
              onChange={(e) => debouncedSearch(e.target.value)}
              className="w-full"
            />
            <Select
              placeholder="Trạng thái QC"
              allowClear
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, status: value, page: 1 }))
              }
              className="w-full"
            >
              <Select.Option value={QCStatusProcess.PENDING}>
                Chờ xử lý
              </Select.Option>
              <Select.Option value={QCStatusProcess.WAITING}>
                Đang chờ
              </Select.Option>
              <Select.Option value={QCStatusProcess.CHECKING}>
                Đang kiểm tra
              </Select.Option>
              <Select.Option value={QCStatusProcess.COMPLETED}>
                Hoàn thành
              </Select.Option>
            </Select>
            <Select
              placeholder="Yêu cầu QC"
              allowClear
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, qcRequested: value, page: 1 }))
              }
              className="w-full"
            >
              <Select.Option value={true}>Đã yêu cầu QC</Select.Option>
              <Select.Option value={false}>Chưa yêu cầu QC</Select.Option>
            </Select>
            <Select
              placeholder="Trạng thái nhận hàng"
              allowClear
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, isReturn: value, page: 1 }))
              }
              className="w-full"
            >
              <Select.Option value={true}>Đã nhận hàng</Select.Option>
              <Select.Option value={false}>Chưa nhận hàng</Select.Option>
            </Select>
          </Space>
        </Card>

        {importGroupLoading ? (
          <div className="flex justify-center items-center py-8">
            <Spin indicator={<LoadingOutlined spin />} size="large" />
          </div>
        ) : importGroups.length > 0 ? (
          <div className="space-y-2">
            {importGroups.map((importGroup) => (
              <Card
                key={importGroup.id}
                size="small"
                className={`cursor-pointer hover:shadow-md transition-shadow ${
                  selectedImportId === importGroup.id
                    ? "border-blue-500 border-2"
                    : ""
                }`}
                onClick={() => setSelectedImportId(importGroup.id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FileTextOutlined className="text-blue-500" />
                      <span className="font-medium text-blue-600 dark:text-blue-300">
                        {importGroup.name}
                      </span>
                      <Tag color="blue" className="ml-auto">
                        <BarcodeOutlined /> #{importGroup.id}
                      </Tag>
                    </div>
                    <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2 text-sm">
                      <div className="flex flex-wrap items-center gap-2 text-gray-600 dark:text-gray-300 col-span-2">
                        <Tag
                          color={importGroup.qcRequested ? "green" : "red"}
                          key="qcRequested"
                        >
                          {importGroup.qcRequested
                            ? "Đã yêu cầu QC"
                            : "Chưa yêu cầu QC"}
                        </Tag>
                        {importGroup && getQCProcessStatus(importGroup.status)}
                        {importGroup.status === QCStatusProcess.COMPLETED && (
                          <Tag
                            color={importGroup.isReturn ? "green" : "orange"}
                            key="isReturn"
                          >
                            {importGroup.isReturn
                              ? "Đã nhận hết hàng"
                              : "Chưa nhận hết hàng"}
                          </Tag>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
            <div className="flex justify-center pt-4">
              <Space>
                <Button
                  disabled={filter.page === 1}
                  onClick={() =>
                    setFilter((prev) => ({
                      ...prev,
                      page: (prev.page || 1) - 1,
                    }))
                  }
                >
                  Trang trước
                </Button>
                <span className="px-4">
                  Trang {filter.page} /{" "}
                  {importGroupList?.pagination?.totalPages || 1}
                </span>
                <Button
                  disabled={
                    filter.page === importGroupList?.pagination?.totalPages
                  }
                  onClick={() =>
                    setFilter((prev) => ({
                      ...prev,
                      page: (prev.page || 1) + 1,
                    }))
                  }
                >
                  Trang sau
                </Button>
              </Space>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">
            Không tìm thấy kết quả
          </div>
        )}
      </div>

      <div className="flex-1 max-h-[calc(100vh-100px)] lg:max-h-screen flex flex-col overflow-auto">
        {selectedImportLoading ? (
          <div className="h-full flex items-center justify-center">
            <Spin indicator={<LoadingOutlined spin />} size="large" />
          </div>
        ) : selectedImport ? (
          <Card
            title={
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <Typography.Title
                  level={4}
                  style={{
                    margin: 0,
                    fontFamily: "Chakra Petch",
                    textTransform: "uppercase",
                  }}
                >
                  Chi tiết nhập kho
                </Typography.Title>
                <Space className="flex flex-wrap gap-2">
                  <Tag color={selectedImport.isReturn ? "green" : "orange"}>
                    {selectedImport.isReturn
                      ? "Đã nhận hết hàng"
                      : "Chưa nhận hết hàng"}
                  </Tag>
                  <Button
                    color="cyan"
                    variant="outlined"
                    onClick={() => setRestockModalOpen(true)}
                  >
                    Phiếu nhận hàng
                  </Button>
                  <Select
                    disabled={selectedImport.priority !== null}
                    value={
                      priority ? priority : selectedImport.priority || undefined
                    }
                    onChange={setPriority}
                    placeholder={"Chọn mức độ ưu tiên"}
                    className="min-w-[160px]"
                  >
                    <Select.Option value={PriorityMark.HIGH}>Cao</Select.Option>
                    <Select.Option value={PriorityMark.MEDIUM}>
                      Trung bình
                    </Select.Option>
                    <Select.Option value={PriorityMark.LOW}>Thấp</Select.Option>
                  </Select>
                  <Popconfirm
                    title="Xác nhận yêu cầu QC?"
                    onConfirm={() => handleQCRequest(selectedImport)}
                    okText="Đồng ý"
                    cancelText="Hủy"
                    description="Bạn có chắc chắn muốn yêu cầu QC cho phiếu nhập này không?"
                    placement="left"
                  >
                    <Button
                      type="primary"
                      disabled={selectedImport.qcRequested || !priority}
                    >
                      {selectedImport.qcRequested
                        ? "Đã yêu cầu QC"
                        : "Yêu cầu QC"}
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            }
            className="h-full"
          >
            <div className="space-y-6">
              <Card
                size="small"
                className="bg-gradient-to-br from-blue-50 to-gray-50 border-blue-200 dark:from-gray-800 dark:to-gray-900 dark:border-gray-700"
              >
                <div className="mb-4 pb-3 border-b border-blue-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Badge status="processing" />
                    <span className="text-lg font-semibold text-blue-700 dark:text-blue-300">
                      {selectedImport.name}
                    </span>
                    <Tag color="blue" className="ml-auto">
                      <BarcodeOutlined /> #{selectedImport.id}
                    </Tag>
                  </div>
                </div>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Mã nhóm">
                    <span className="text-gray-800 dark:text-gray-200">
                      {selectedImport.code}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="AWB">
                    <span className="text-gray-800 dark:text-gray-200">
                      {selectedImport.awb}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Ngày tạo">
                    <span className="text-gray-800 dark:text-gray-200">
                      {new Date(selectedImport.createdAt).toLocaleString()}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Mô tả" span={2}>
                    <span className="text-gray-800 dark:text-gray-300">
                      {selectedImport.description}
                    </span>
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <div>
                <Typography.Title level={5} className="flex items-center gap-2">
                  <BarcodeOutlined className="text-blue-500" />
                  Danh sách mã hàng
                </Typography.Title>
                <div className="overflow-x-auto">
                  <Table
                    columns={detailColumns}
                    dataSource={selectedImport.partNumbers}
                    pagination={false}
                    size="small"
                    rowKey="id"
                    bordered
                    scroll={{ x: "max-content" }}
                    className="[&_.ant-table-cell]:!border-gray-300"
                  />
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            Chọn một phiếu nhập để xem chi tiết
          </div>
        )}
      </div>

      <ModalRestockWarehouse
        open={restockModalOpen}
        onClose={() => setRestockModalOpen(false)}
        groupId={selectedImportId}
      />
    </div>
  );
};

export default WarehouseListImport;
